import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IDirecteur, defaultValue } from 'app/shared/model/directeur.model';

const initialState: EntityState<IDirecteur> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IDirecteur>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/directeurs';

// Actions

export const getDirecteurs = createAsyncThunk(
  'directeur/fetch_directeur_list',
  async (id: string | number) => {
    return axios.get<IDirecteur[]>(`${apiUrl}?ecoleId=${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const getDirecteur = createAsyncThunk(
  'directeur/fetch_directeur',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IDirecteur>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createDirecteur = createAsyncThunk(
  'directeur/create_directeur',
  async (entity: IDirecteur, thunkAPI) => {
    const result = await axios.post<IDirecteur>(
      `${apiUrl}/${entity.ecoleId}?directeurAuthority=${entity.directeurAuthority}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getDirecteurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateDirecteur = createAsyncThunk(
  'directeur/update_directeur',
  async (entity: IDirecteur, thunkAPI) => {
    const result = await axios.put<IDirecteur>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getDirecteurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteDirecteur = createAsyncThunk(
  'directeur/delete_directeur',
  async (entity: IDirecteur, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IDirecteur>(requestUrl);
    thunkAPI.dispatch(getDirecteurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export type DirecteurState = Readonly<typeof initialState>;
// slice

export const DirecteurSlice = createSlice({
  name: 'directeur',
  initialState: initialState as DirecteurState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getDirecteur.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteDirecteur.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        // state.entity = {};
      })
      .addMatcher(isFulfilled(getDirecteurs), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createDirecteur, updateDirecteur), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getDirecteurs, getDirecteur), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createDirecteur, updateDirecteur, deleteDirecteur), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = DirecteurSlice.actions;

// Reducer
export default DirecteurSlice.reducer;
